/**
 * FlahaFast Redux Store Configuration
 *
 * Workflow slice is persisted to sessionStorage so Stage 1–3 data
 * survives refresh within the same browser tab (P0).
 */

import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import waterQualityReducer from './slices/waterQualitySlice';
import saltLibraryReducer from './slices/saltLibrarySlice';
import formulationReducer from './slices/formulationSlice';
import calculationReducer from './slices/calculationSlice';
import resultsReducer from './slices/resultsSlice';
import elementsReducer from './slices/elementSlice';
import cropRecommendationReducer from './slices/cropRecommendationSlice';
import authReducer from './slices/authSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import workflowReducer from './slices/workflowSlice';
import type { WorkflowState } from './slices/workflowSlice';

const WORKFLOW_PERSIST_KEY = 'flahafast.workflow.v1';

function loadWorkflowState(): WorkflowState | undefined {
  try {
    if (typeof sessionStorage === 'undefined') return undefined;
    const raw = sessionStorage.getItem(WORKFLOW_PERSIST_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as WorkflowState;
  } catch {
    return undefined;
  }
}

function saveWorkflowState(state: WorkflowState): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    // Drop ephemeral UI flags that should not restore as loading/error
    const toSave: WorkflowState = {
      ...state,
      loading: false,
      error: null,
      showValidationErrors: false,
    };
    sessionStorage.setItem(WORKFLOW_PERSIST_KEY, JSON.stringify(toSave));
  } catch {
    // quota / private mode — ignore
  }
}

const rootReducer = combineReducers({
  auth: authReducer,
  subscription: subscriptionReducer,
  workflow: workflowReducer,
  waterQuality: waterQualityReducer,
  saltLibrary: saltLibraryReducer,
  formulation: formulationReducer,
  calculation: calculationReducer,
  results: resultsReducer,
  elements: elementsReducer,
  cropRecommendation: cropRecommendationReducer,
});

const preloadedWorkflow = loadWorkflowState();

const store = configureStore({
  reducer: rootReducer,
  preloadedState: preloadedWorkflow
    ? ({ workflow: preloadedWorkflow } as Partial<ReturnType<typeof rootReducer>>)
    : undefined,
});

store.subscribe(() => {
  saveWorkflowState(store.getState().workflow);
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export default store;